import {Autocomplete, Button, Grid, Paper, TextField, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {FilterAltRounded, ReplayRounded, SearchRounded} from "@mui/icons-material";
import {useRecoilState} from "recoil";
import React, {useContext, useEffect, useState} from "react";
import { operatorFromValues, operatorList } from "../state/operatorState";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import axios from "axios";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useQuery} from "@tanstack/react-query";
import CustomLoader from "../../../util/component/CustomLoader";

export interface IOperatorSearchProps {
    legalEntities?: any[];
    dealers?: any[];
    mobilityStores?: any[];
    username?: string | null;
    name?: string | null;
    surname?: string | null;
    email?: string | null;
}

const OperatorSearch = () => {
    const theme = useTheme();
    const {t} = useTranslation(['operator']);
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [formValues, setFormValues] = useRecoilState<any>(operatorFromValues);
    const [legalEntities] = useState<any[]>(globalData.user?.legalEntities);
    const [dealers, setDealers] = useState<any[]>([]);
    const [mobilityStores, setMobilityStores] = useState<any[]>([]);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);

    const validationSchema = yup.object().shape({
        legalEntities: yup
            .array().min(1, 'message.validation.selectAtLeastOne'),
        username: yup
            .string().nullable(true).trim().lowercase(),
        name: yup
            .string().nullable(true).trim().lowercase(),
        surname: yup
            .string().nullable(true).trim().lowercase(),
        email: yup
            .string().nullable(true).trim().lowercase()
            .email('message.validation.emailNotValid'),
    }).test(
        'atLeastOneRequired',
        function (values) {
            if (!(values.legalEntities?.length !== 0 || values.username || values.name || values.surname || values.email)) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.validation.atLeastOneRequired'),
                            type: snackbarTypes.warning,
                            show: true,
                            autoClose: true
                        }
                    }
                });
                return false;
            } else {
                return true;
            }
        }
    )

    const { handleSubmit, formState: {errors}, setValue, control, reset} = useForm<IOperatorSearchProps>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            legalEntities: legalEntities.length === 1 ? legalEntities : [],
            dealers: dealers.length === 1 ? dealers : [],
            mobilityStores: mobilityStores.length === 1 ? mobilityStores : [],
            username: null,
            name: null,
            surname: null,
            email: null,
        }
    });

    const onSubmit = (params: any) => {
        let operatorToSearch = {
            legalEntities: legalEntities,
            dealers: dealers,
            mobilityStores: mobilityStores,
        };
        operatorToSearch = {...operatorToSearch, ...params};
        setFormValues(operatorToSearch);
    };

    useEffect(() => {
        if (sessionStorage.getItem('operatorSearch') !== null && formValues === null) {
            let formValuesTmp = JSON.parse(sessionStorage.getItem('operatorSearch') as string);
            let tmpDealers = globalData.user?.dealers?.filter((dealer: any) => {
                if (formValuesTmp?.legalEntities?.length > 0) {
                    return dealer.legalEntities.some((le: any) =>
                        formValuesTmp.legalEntities.find((fel: any) => fel.id === le.id)
                    );
                } else {
                    return true;
                }
            });
            setDealers(tmpDealers);
            let tmpMobilityStores = globalData.user?.mobilityStores?.filter((mobilityStore: any) => {
                //@ts-ignore
                if (formValuesTmp?.legalEntities?.length > 0) {
                    return formValuesTmp?.legalEntities?.find((legalEntity: any) => legalEntity.name === mobilityStore.tenant);
                } else {
                    return mobilityStore;
                }
            });
            setMobilityStores(tmpMobilityStores);

            setValue('legalEntities', formValuesTmp.legalEntities);
            setValue('dealers', formValuesTmp.dealers);
            setValue('mobilityStores', formValuesTmp.mobilityStores);
            setValue('username', formValuesTmp.username);
            setValue('name', formValuesTmp.name);
            setValue('surname', formValuesTmp.surname);
            setValue('email', formValuesTmp.email);
            setFormValues(formValuesTmp);
            sessionStorage.removeItem('operatorSearch');
        }
    }, [formValues]);

    useEffect(() => {
        if (legalEntities.length === 1) {
            setDealers(globalData.user?.dealers);
            setMobilityStores(globalData.user?.mobilityStores);
        }
    }, []);

    useEffect(() => {
        if (formValues !== null) {
            sessionStorage.setItem('operatorSearch', JSON.stringify(formValues));
        }
    }, [formValues]);

    return (
        <Paper style={{
            borderRadius: "4px",
            padding: theme.spacing(1) + ' ' + theme.spacing(2),
            marginBottom: theme.spacing(2)
        }}
               elevation={1}
        >
            <form
                onSubmit={handleSubmit(onSubmit)}
            >
                <Grid container
                      spacing={theme.spacing(2)}
                      alignItems="flex-end"
                >
                    {legalEntities.length > 1 &&
                        <Grid item md={3} xs={12}>
                            <Controller
                                control={control}
                                name="legalEntities"
                                render={({field: {onChange, value}}: any) => (
                                    <Autocomplete
                                        limitTags={1}
                                        disableCloseOnSelect={true}
                                        multiple
                                        id={'legalEntities'}
                                        options={legalEntities}
                                        value={value || []}
                                        getOptionLabel={(option: any) => t('translation:legalEntities.'.concat(option.name))}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField {...params} label={t("label.legalEntity")}
                                                       error={Boolean(errors.legalEntities)}
                                                       helperText={t(errors.legalEntities?.message as string)}
                                            />
                                        )}
                                        onChange={(event, value) => {
                                            let tmpDealers = globalData.user?.dealers?.filter((dealer: any) => {
                                                if (value?.length > 0) {
                                                    return dealer.legalEntities.some((le: any) =>
                                                        value.find((selectedLe: any) => selectedLe.id === le.id)
                                                    );
                                                } else {
                                                    return true;
                                                }
                                            });
                                            setDealers(tmpDealers);

                                            let tmpMobilityStores = globalData.user?.mobilityStores?.filter((mobilityStore: any) => {
                                                if (value?.length > 0) {
                                                    return value?.find((legalEntity: any) => legalEntity.name === mobilityStore.tenant);
                                                } else {
                                                    return mobilityStore;
                                                }
                                            });
                                            setMobilityStores(tmpMobilityStores);
                                            setValue('dealers', []);
                                            setValue('mobilityStores', []);
                                            onChange( value);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    }
                    {globalData.user?.dealers.length > 1 &&
                        <Grid item md={3} xs={12}>
                            <Controller
                                control={control}
                                name="dealers"
                                render={({field: {onChange, value}}: any) => (
                                    <Autocomplete
                                        limitTags={1}
                                        disableCloseOnSelect={true}
                                        multiple
                                        id={'dealers'}
                                        options={dealers}
                                        value={value || []}
                                        getOptionLabel={(option: any) => t('translation:dealers.'.concat(option.name))}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField {...params} label={t("label.dealer")}
                                                       error={Boolean(errors.dealers)}
                                                       helperText={t(errors.dealers?.message as string)}
                                            />
                                        )}
                                        onChange={(event, value) => {
                                            onChange( value);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    }
                    {globalData.user?.mobilityStores.length > 1 &&
                        <Grid item md={3} xs={12}>
                            <Controller
                                control={control}
                                name="mobilityStores"
                                render={({field: {onChange, value}}: any) => (
                                    <Autocomplete
                                        limitTags={1}
                                        disableCloseOnSelect={true}
                                        multiple
                                        id={'mobilityStores'}
                                        options={mobilityStores}
                                        value={value || []}
                                        getOptionLabel={(option: any) => option.name}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField {...params} label={t("label.mobilityStore")}
                                                       error={Boolean(errors.mobilityStores)}
                                                       helperText={t(errors.mobilityStores?.message as string)}
                                            />
                                        )}
                                        onChange={(event, value) => {
                                            onChange( value);
                                        }}
                                    />
                                )}
                            />

                        </Grid>
                    }
                    <Grid item md={3} xs={12}>
                        <Controller
                            control={control}
                            name="username"
                            render={({field: {onChange, value}}: any) => (
                                <TextField
                                    fullWidth
                                    id="username"
                                    name="username"
                                    type="text"
                                    label={t('label.username')}
                                    error={Boolean(errors.username)}
                                    helperText={t(errors.username?.message as string)}
                                    value={value || ''}
                                    onChange={onChange}
                                />
                            )}
                        />
                    </Grid>
                    {showAdvancedFilters &&
                                <>
                                    <Grid item md={3} xs={12}>
                                        <Controller
                                            control={control}
                                            name="name"
                                            render={({field: {onChange, value}}: any) => (
                                                <TextField
                                                    fullWidth
                                                    id="name"
                                                    name="name"
                                                    type="text"
                                                    label={t('label.name')}
                                                    error={Boolean(errors.name)}
                                                    helperText={t(errors.name?.message as string)}
                                                    value={value || ''}
                                                    onChange={onChange}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item md={3} xs={12}>
                                        <Controller
                                            control={control}
                                            name="surname"
                                            render={({field: {onChange, value}}: any) => (
                                                <TextField
                                                    fullWidth
                                                    id="surname"
                                                    name="surname"
                                                    type="text"
                                                    label={t('label.surname')}
                                                    error={Boolean(errors.surname)}
                                                    helperText={t(errors.surname?.message as string)}
                                                    value={value || ''}
                                                    onChange={onChange}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item md={3} xs={12}>
                                        <Controller
                                            control={control}
                                            name="email"
                                            render={({field: {onChange, value}}: any) => (
                                                <TextField
                                                    fullWidth
                                                    id="email"
                                                    name="email"
                                                    type="text"
                                                    label={t('label.email')}
                                                    error={Boolean(errors.email)}
                                                    helperText={t(errors.email?.message as string)}
                                                    value={value || ''}
                                                    onChange={onChange}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </>
                    }

                    <Grid item xs={12}>
                        <Grid container spacing={theme.spacing(2)}
                              direction="row"
                              justifyContent="flex-end"
                              alignItems="flex-end"
                        >
                            <Grid item>
                                <Button variant={'text'}
                                        color={"accent"}
                                        startIcon={<ReplayRounded />}
                                        onClick={() => {
                                            reset();
                                            setFormValues(null);
                                            sessionStorage.removeItem('operatorSearch');
                                        }}>
                                    {t('action.reset')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button variant={'text'}
                                        color={"accent"}
                                        startIcon={<FilterAltRounded />}
                                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                                    {
                                        showAdvancedFilters ? t('action.close') : t('label.advancedFilters')
                                    }
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button type={'submit'} size={'icon'} color={'accent'}>
                                    <SearchRounded/>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </Paper>
    );
}

export default OperatorSearch;
